import './App.css';
import {
  FormEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement
} from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  API_BASE_URL,
  AUTH_BASE_URL,
  AUTH_ME_URL,
  DEFAULT_LIST_PAGE_SIZE,
  PO_REGISTRY_URL,
  PO_STATUS_COUNTS_URL,
  REQUEST_STATUS_COUNTS_URL,
  UI_POLL_INTERVAL_MS,
  VENDORS_API_URL,
  VENDORS_PAGE_URL,
  DELIVERY_ADDRESSES_API_URL,
  WELLS_PAGE_URL,
  WELLS_REGION_COUNTS_URL,
  MASTER_WELLS_API_URL,
  REGIONS_LIST_URL,
  RFQ_API_URL,
  RFQ_STATUS_COUNTS_URL,
  OPERATIONS_RFQ_API_URL,
  OPERATIONS_RFQ_STATUS_COUNTS_URL,
  poApiUrl,
  purchaseOrderPdfUrl,
  requestPdfUrl
} from './constants';
import { AppFooter } from './components/layout/AppFooter';
import {
  buildCreateApiPayload,
  buildUpdateDraftApiPayload,
  emptyForm,
  normalizeProcurementRequest,
  primaryMaterialLine,
  requestToFormState,
  requestorFormWithUser,
  validateDraftSave,
  validateSubmitForm,
  validateWellSelectedFromDirectory,
  getSubmitValidationFieldErrors,
  SUBMIT_FIELD_WELL_DIRECTORY,
  mapApiDeliveryAddress,
  mapApiVendor,
  mapApiWell,
  mapMasterWellToPicker,
  type CurrentUserProfile,
  type LoginResponse,
  type PoFormState,
  type PoStatusFilter,
  type PoTileCounts,
  type ProcurementRequest,
  type ProcurementRequestsPageDto,
  type ProcurementRequestStatusCountsDto,
  type PurchaseOrder,
  type PurchaseOrderStatusCountsDto,
  type PurchaseOrdersPageDto,
  type RequestCounts,
  type UpdatePurchaseOrderPayload,
  type RequestStatus,
  type StatusFilter,
  type DeliveryAddressDirectoryEntry,
  type VendorDirectoryEntry,
  type WellMaster,
  type WellRegionCount,
  type RegionFieldDto,
  type RegionMasterDto,
  type RequestForQuoteDetail,
  type RequestForQuoteSummary,
  type RfqStatusFilter,
  type RfqTileCounts,
  mapApiRequestForQuote,
  mapApiRfqSummary,
  rfqDetailToSummary
} from './domain/types';
import { normalizeRfqStatus } from './domain/requestForQuote';
import { materialInventoryNeed } from './domain/procurementRequest';
import {
  canLoadDeliveryAddressDirectory,
  canPerformProcurementStaffActions,
  canViewProcurementModules,
  normalizePurchaseOrder,
  parseOptionalUsdAmount
} from './domain/formatters';
import { downloadBlob, parseContentDispositionFilename, readPdfExportErrorDetail } from './utils/pdfDownload';
import { Routes, Route, matchPath, useLocation, useNavigate } from 'react-router-dom';
import { LoginPage } from './pages/LoginPage';
import { AuthenticatedPage } from './pages/AuthenticatedPage';
import { BooksModulePage } from './pages/BooksModulePage';
import { AccountingPage } from './pages/AccountingPage';
import { CommercialPage } from './pages/CommercialPage';
import { AdminModulePage } from './pages/AdminModulePage';
import { ContractsPage } from './pages/ContractsPage';
import { VendorQuotePage } from './pages/VendorQuotePage';
import { clearStoredAuth, readStoredAuth, writeStoredAuth } from './authStorage';
import { acquireEntraApiAccessToken, isEntraLoginAvailable, signOutEntraPopup } from './auth/entraAuth';
import { nextSortState } from './utils/gridSort';

function mapRegionApiRow(raw: Record<string, unknown>): RegionMasterDto {
  const fieldsRaw = raw.fields;
  const fields: RegionFieldDto[] = Array.isArray(fieldsRaw)
    ? (fieldsRaw as Record<string, unknown>[]).map((f) => ({
        id: String(f.id ?? ''),
        name: String(f.name ?? ''),
        county: String(f.county ?? ''),
        state: String(f.state ?? ''),
        closestCity: String(f.closestCity ?? ''),
        milesFromCity:
          f.milesFromCity === undefined || f.milesFromCity === null ? undefined : Number(f.milesFromCity)
      }))
    : [];
  const daRaw = raw.deliveryAddresses;
  const deliveryAddresses = Array.isArray(daRaw)
    ? (daRaw as Record<string, unknown>[]).map((d) => {
        const c = String(d.category ?? '').toLowerCase();
        const category: 'Yard' | 'WellPad' =
          c === 'wellpad' || c === 'well_pad' ? 'WellPad' : 'Yard';
        return {
          id: String(d.id ?? ''),
          label: String(d.label ?? ''),
          address: String(d.address ?? ''),
          category
        };
      })
    : [];
  return {
    id: String(raw.id ?? ''),
    code: String(raw.code ?? ''),
    name: String(raw.name ?? ''),
    isActive: Boolean(raw.isActive),
    sortOrder: Number(raw.sortOrder ?? 0),
    fields,
    deliveryAddresses
  };
}

function requestCountsFromStatusMap(sc: Record<string, number>): RequestCounts {
  const n = (k: string): number => sc[k] ?? 0;
  const draft = n('draft');
  const pendingApproval = n('pendingApproval');
  const procurementReview = n('procurementReview');
  const sourcing = n('sourcing');
  const orderPlaced = n('orderPlaced');
  const procured = n('procured');
  const complete = n('complete');
  const rejected = n('rejected');
  const all =
    draft + pendingApproval + procurementReview + sourcing + orderPlaced + procured + complete + rejected;
  return { all, draft, pendingApproval, procurementReview, sourcing, orderPlaced, procured, complete, rejected };
}

function requestsListUrl(
  page: number,
  pageSize: number,
  statusFilter: StatusFilter,
  sortColumn: string | null,
  sortDirection: 'asc' | 'desc' | null
): string {
  const params = new URLSearchParams();
  params.set('page', String(page));
  params.set('pageSize', String(pageSize));
  if (statusFilter !== 'all') {
    params.set('status', statusFilter);
  }
  if (sortColumn === 'created' && sortDirection) {
    params.set('sortBy', 'created');
    params.set('sortDir', sortDirection);
  } else if (sortColumn === 'requiredBy' && sortDirection) {
    params.set('sortBy', 'requiredBy');
    params.set('sortDir', sortDirection);
  }
  return `${API_BASE_URL}?${params.toString()}`;
}

function poTileCountsFromMap(sc: Record<string, number>): PoTileCounts {
  const created = sc.created ?? 0;
  const paid = sc.paid ?? 0;
  return { all: created + paid, created, paid };
}

function num(sc: Record<string, number>, k: string): number {
  const v = sc[k];
  return typeof v === 'number' && !Number.isNaN(v) ? v : 0;
}

function rfqTileCountsFromProcurementMap(sc: Record<string, number>): RfqTileCounts {
  return {
    all: num(sc, 'all'),
    draft: num(sc, 'draft'),
    requestSent: num(sc, 'requestSent'),
    partialReceived: num(sc, 'partialReceived'),
    received: num(sc, 'received'),
    pendingApproval: num(sc, 'pendingApproval'),
    approved: num(sc, 'approved'),
    rejected: num(sc, 'rejected'),
    cancelled: num(sc, 'cancelled'),
    complete: num(sc, 'complete')
  };
}

function rfqTileCountsFromOperationsMap(sc: Record<string, number>): RfqTileCounts {
  return {
    all: num(sc, 'all'),
    draft: 0,
    requestSent: 0,
    partialReceived: 0,
    received: 0,
    pendingApproval: num(sc, 'pendingApproval'),
    approved: num(sc, 'approved'),
    rejected: num(sc, 'rejected'),
    cancelled: 0,
    complete: 0
  };
}

function emptyRfqTileCounts(): RfqTileCounts {
  return {
    all: 0,
    draft: 0,
    requestSent: 0,
    partialReceived: 0,
    received: 0,
    pendingApproval: 0,
    approved: 0,
    rejected: 0,
    cancelled: 0,
    complete: 0
  };
}

function AppAuthenticatedRoutes(props: React.ComponentProps<typeof AuthenticatedPage>): ReactElement {
  return (
    <Routes>
      <Route path="/" element={<AuthenticatedPage {...props} />} />
      <Route path="/requests" element={<AuthenticatedPage {...props} />} />
      <Route path="/requests/:requestId" element={<AuthenticatedPage {...props} />} />
      <Route path="/purchase-orders" element={<AuthenticatedPage {...props} />} />
      <Route path="/purchase-orders/:poId" element={<AuthenticatedPage {...props} />} />
      <Route path="/request-for-quotes" element={<AuthenticatedPage {...props} />} />
      <Route path="/request-for-quotes/:rfqId" element={<AuthenticatedPage {...props} />} />
      <Route path="/wells" element={<AuthenticatedPage {...props} />} />
      <Route path="/assistant" element={<AuthenticatedPage {...props} />} />
      <Route path="/assistant/:conversationId" element={<AuthenticatedPage {...props} />} />
      <Route path="/search" element={<AuthenticatedPage {...props} />} />
      <Route path="/vendors" element={<AuthenticatedPage {...props} />} />
      <Route
        path="/admin"
        element={<AdminModulePage auth={props.auth} onLogout={props.onLogout} authFetch={props.authFetch} />}
      />
      <Route path="/inventory" element={<AuthenticatedPage {...props} />} />
      <Route path="/material-transfers" element={<AuthenticatedPage {...props} />} />
      <Route path="/material-transfers/new" element={<AuthenticatedPage {...props} />} />
      <Route path="/material-transfers/:transferId/edit" element={<AuthenticatedPage {...props} />} />
      <Route
        path="/contracts"
        element={<ContractsPage auth={props.auth} onLogout={props.onLogout} authFetch={props.authFetch} />}
      />
      <Route
        path="/contracts/new"
        element={<ContractsPage auth={props.auth} onLogout={props.onLogout} authFetch={props.authFetch} />}
      />
      <Route
        path="/contracts/:contractId"
        element={<ContractsPage auth={props.auth} onLogout={props.onLogout} authFetch={props.authFetch} />}
      />
      <Route
        path="/commercial"
        element={<CommercialPage auth={props.auth} onLogout={props.onLogout} authFetch={props.authFetch} />}
      />
      <Route
        path="/commercial/new"
        element={<CommercialPage auth={props.auth} onLogout={props.onLogout} authFetch={props.authFetch} />}
      />
      <Route
        path="/commercial/:runId"
        element={<CommercialPage auth={props.auth} onLogout={props.onLogout} authFetch={props.authFetch} />}
      />
      <Route
        path="/accounting"
        element={<AccountingPage auth={props.auth} onLogout={props.onLogout} authFetch={props.authFetch} />}
      />
      <Route
        path="/accounting/compare"
        element={<AccountingPage auth={props.auth} onLogout={props.onLogout} authFetch={props.authFetch} />}
      />
      <Route
        path="/accounting/ingest"
        element={<AccountingPage auth={props.auth} onLogout={props.onLogout} authFetch={props.authFetch} />}
      />
      <Route
        path="/accounting/doi"
        element={<AccountingPage auth={props.auth} onLogout={props.onLogout} authFetch={props.authFetch} />}
      />
      <Route
        path="/accounting/books"
        element={<AccountingPage auth={props.auth} onLogout={props.onLogout} authFetch={props.authFetch} />}
      />
      <Route
        path="/accounting/demo"
        element={<AccountingPage auth={props.auth} onLogout={props.onLogout} authFetch={props.authFetch} />}
      />
      <Route
        path="/accounting/accounts"
        element={<AccountingPage auth={props.auth} onLogout={props.onLogout} authFetch={props.authFetch} />}
      />
      <Route
        path="/accounting/afes"
        element={<AccountingPage auth={props.auth} onLogout={props.onLogout} authFetch={props.authFetch} />}
      />
      <Route
        path="/accounting/indexes"
        element={<AccountingPage auth={props.auth} onLogout={props.onLogout} authFetch={props.authFetch} />}
      />
      <Route
        path="/accounting/ppa"
        element={<AccountingPage auth={props.auth} onLogout={props.onLogout} authFetch={props.authFetch} />}
      />
      <Route
        path="/master"
        element={<BooksModulePage auth={props.auth} onLogout={props.onLogout} authFetch={props.authFetch} />}
      />
      <Route
        path="/master/:section"
        element={<BooksModulePage auth={props.auth} onLogout={props.onLogout} authFetch={props.authFetch} />}
      />
      <Route
        path="/master/:section/:id"
        element={<BooksModulePage auth={props.auth} onLogout={props.onLogout} authFetch={props.authFetch} />}
      />
    </Routes>
  );
}

function purchaseOrdersListUrl(page: number, pageSize: number, poStatusFilter: PoStatusFilter): string {
  const params = new URLSearchParams();
  params.set('page', String(page));
  params.set('pageSize', String(pageSize));
  if (poStatusFilter !== 'all') {
    params.set('status', poStatusFilter);
  }
  return `${PO_REGISTRY_URL}?${params.toString()}`;
}

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const path = location.pathname;
  /** matchPath + pathname: reliable outside <Route>; useMatch from the app shell can miss /requests/:id. */
  const rfqPageMatch = matchPath({ path: '/request-for-quotes/:rfqId', end: true }, path);
  const routeRfqId = rfqPageMatch?.params.rfqId ?? null;
  const requestPageMatch = matchPath({ path: '/requests/:requestId', end: true }, path);
  const requestIdFromRoute = requestPageMatch?.params.requestId ?? null;

  const [auth, setAuth] = useState<LoginResponse | null>(() => readStoredAuth());
  const [loginUsername, setLoginUsername] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [requests, setRequests] = useState<ProcurementRequest[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [panelComment, setPanelComment] = useState('');
  const [draftEditForm, setDraftEditForm] = useState(() => emptyForm());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [formData, setFormData] = useState(() => emptyForm());
  const [purchaseOrder, setPurchaseOrder] = useState<PurchaseOrder | null>(null);
  const [vendors, setVendors] = useState<VendorDirectoryEntry[]>([]);
  const [deliveryAddresses, setDeliveryAddresses] = useState<DeliveryAddressDirectoryEntry[]>([]);
  const [poForm, setPoForm] = useState<PoFormState>({
    vendorId: '',
    vendorName: '',
    deliveryAddressId: '',
    incoterms: '',
    unitCost: '',
    lineDescription: '',
    freightAmount: '',
    miscellaneousFeeAmount: ''
  });
  const [purchaseOrdersList, setPurchaseOrdersList] = useState<PurchaseOrder[]>([]);
  const [selectedPoId, setSelectedPoId] = useState<string | null>(null);
  const [poRegistryLoading, setPoRegistryLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [requestsPage, setRequestsPage] = useState(1);
  const [requestsPageSize, setRequestsPageSize] = useState(DEFAULT_LIST_PAGE_SIZE);
  const [requestsTotalCount, setRequestsTotalCount] = useState(0);
  const [requestsSort, setRequestsSort] = useState<{
    column: string | null;
    direction: 'asc' | 'desc' | null;
  }>({ column: null, direction: null });
  const [requestsStatusCounts, setRequestsStatusCounts] = useState<Record<string, number>>({});
  const [poPage, setPoPage] = useState(1);
  const [poPageSize, setPoPageSize] = useState(DEFAULT_LIST_PAGE_SIZE);
  const [poTotalCount, setPoTotalCount] = useState(0);

  const handleRequestsPageSizeChange = useCallback((pageSize: number): void => {
    setRequestsPageSize(pageSize);
    setRequestsPage(1);
  }, []);

  const handleRequestsSortClick = useCallback((columnId: string): void => {
    setRequestsSort((prev) => {
      const next = nextSortState(prev.column, prev.direction, columnId);
      return { column: next.column, direction: next.direction };
    });
  }, []);

  const handlePoPageSizeChange = useCallback((pageSize: number): void => {
    setPoPageSize(pageSize);
    setPoPage(1);
  }, []);

  const [wellsPage, setWellsPage] = useState(1);
  const [wellsPageSize, setWellsPageSize] = useState(DEFAULT_LIST_PAGE_SIZE);
  const [wellsPagedTotal, setWellsPagedTotal] = useState(0);
  const [wellsPaged, setWellsPaged] = useState<WellMaster[]>([]);
  const [wellsPagedLoading, setWellsPagedLoading] = useState(false);
  const [wellRegionFilter, setWellRegionFilter] = useState<string | null>(null);
  const [wellRegionCounts, setWellRegionCounts] = useState<WellRegionCount[]>([]);

  const handleWellsPageSizeChange = useCallback((pageSize: number): void => {
    setWellsPageSize(pageSize);
    setWellsPage(1);
  }, []);

  const [vendorsPage, setVendorsPage] = useState(1);
  const [vendorsPageSize, setVendorsPageSize] = useState(DEFAULT_LIST_PAGE_SIZE);
  const [vendorsPagedTotal, setVendorsPagedTotal] = useState(0);
  const [vendorsPaged, setVendorsPaged] = useState<VendorDirectoryEntry[]>([]);
  const [vendorsPagedLoading, setVendorsPagedLoading] = useState(false);

  const handleVendorsPageSizeChange = useCallback((pageSize: number): void => {
    setVendorsPageSize(pageSize);
    setVendorsPage(1);
  }, []);

  const setWellRegionFilterAndResetPage = useCallback((region: string | null): void => {
    setWellRegionFilter(region);
    setWellsPage(1);
  }, []);

  const [poStatusFilter, setPoStatusFilter] = useState<PoStatusFilter>('all');
  const [poStatusCounts, setPoStatusCounts] = useState<Record<string, number>>({});
  const [wells, setWells] = useState<WellMaster[]>([]);
  const [activeRegions, setActiveRegions] = useState<RegionMasterDto[]>([]);
  const [newRequestModalOpen, setNewRequestModalOpen] = useState(false);
  const [newRequestSubmitFieldErrors, setNewRequestSubmitFieldErrors] = useState<string[]>([]);
  const [draftSubmitFieldErrors, setDraftSubmitFieldErrors] = useState<string[]>([]);
  const [detailRequestOverride, setDetailRequestOverride] = useState<ProcurementRequest | null>(null);
  const [requestDetailNotFound, setRequestDetailNotFound] = useState(false);
  const [detailPoOverride, setDetailPoOverride] = useState<PurchaseOrder | null>(null);
  const [rfqDetail, setRfqDetail] = useState<RequestForQuoteDetail | null>(null);
  const [rfqLoading, setRfqLoading] = useState(false);
  const [registryRfqDetail, setRegistryRfqDetail] = useState<RequestForQuoteDetail | null>(null);
  const [registryRfqDetailLoading, setRegistryRfqDetailLoading] = useState(false);
  const [rfqListRows, setRfqListRows] = useState<RequestForQuoteSummary[]>([]);
  const [rfqListLoading, setRfqListLoading] = useState(false);
  const [rfqListPage, setRfqListPage] = useState(1);
  const [rfqListPageSize, setRfqListPageSize] = useState(DEFAULT_LIST_PAGE_SIZE);
  const [rfqListTotal, setRfqListTotal] = useState(0);
  const [rfqStatusFilter, setRfqStatusFilter] = useState<RfqStatusFilter>('all');
  const [rfqStatusCounts, setRfqStatusCounts] = useState<Record<string, number>>({});
  const [selectedRfqRegistryId, setSelectedRfqRegistryId] = useState<string | null>(null);
  const [poContextRequestForRfq, setPoContextRequestForRfq] = useState<ProcurementRequest | null>(null);
  /** PO shown on RFQ registry / full page when RFQ status is complete. */
  const [rfqRegistryLinkedPo, setRfqRegistryLinkedPo] = useState<PurchaseOrder | null>(null);
  const hasHydratedRequestsRef = useRef(false);
  const previousStatusByIdRef = useRef<Record<string, RequestStatus>>({});

  const notifySuccess = useCallback((text: string): void => {
    toast.success(text);
  }, []);
  const notifyError = useCallback((text: string): void => {
    toast.error(text);
  }, []);
  const notifyInfo = useCallback((text: string): void => {
    toast.info(text);
  }, []);

  /** URL is the source of truth on /requests/:requestId — state can lag one frame behind navigation from RFQ. */
  const effectiveRequestId = requestIdFromRoute ?? selectedId;

  useLayoutEffect(() => {
    if (requestIdFromRoute) {
      setSelectedId(requestIdFromRoute);
    }
  }, [requestIdFromRoute]);

  const selectedRequest = useMemo(() => {
    const id = effectiveRequestId;
    if (!id) {
      return null;
    }
    const fromList = requests.find((r) => r.id === id);
    if (fromList) {
      return fromList;
    }
    if (detailRequestOverride?.id === id) {
      return detailRequestOverride;
    }
    return null;
  }, [requests, effectiveRequestId, detailRequestOverride]);
  const directoryWellKeys = useMemo(
    () => new Set(wells.map((w) => w.id.trim().toLowerCase())),
    [wells]
  );

  const exportSelectedRequestPdf = useCallback(async (): Promise<void> => {
    if (!selectedRequest?.id || !auth?.token) {
      return;
    }
    try {
      const response = await fetch(requestPdfUrl(selectedRequest.id), {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      if (response.status === 401) {
        clearStoredAuth();
        setAuth(null);
        setError('Session expired. Please sign in again.');
        notifyError('Session expired. Please sign in again.');
        navigate('/', { replace: true });
        return;
      }
      if (!response.ok) {
        const detail = await readPdfExportErrorDetail(response);
        notifyError(
          detail
            ? `Could not export request PDF (HTTP ${response.status}). ${detail}`
            : `Could not export request PDF (HTTP ${response.status}).`
        );
        return;
      }
      const blob = await response.blob();
      const safe =
        (selectedRequest.requestId ?? selectedRequest.id).replace(/[^a-zA-Z0-9._-]+/g, '-').replace(/^-+|-+$/g, '') ||
        'export';
      const name =
        parseContentDispositionFilename(response.headers.get('Content-Disposition')) ?? `${safe}.pdf`;
      downloadBlob(blob, name);
      notifySuccess('Request exported to PDF.');
    } catch {
      notifyError('Could not export request PDF.');
    }
  }, [selectedRequest, auth?.token, navigate, notifySuccess, notifyError]);

  const exportPurchaseOrderPdfFor = useCallback(
    async (po: PurchaseOrder): Promise<void> => {
      if (!auth?.token || !po.id) {
        return;
      }
      try {
        const response = await fetch(purchaseOrderPdfUrl(po.id), {
          headers: { Authorization: `Bearer ${auth.token}` }
        });
        if (response.status === 401) {
          clearStoredAuth();
          setAuth(null);
          setError('Session expired. Please sign in again.');
          notifyError('Session expired. Please sign in again.');
          navigate('/', { replace: true });
          return;
        }
        if (!response.ok) {
          const detail = await readPdfExportErrorDetail(response);
          notifyError(
            detail
              ? `Could not export purchase order PDF (HTTP ${response.status}). ${detail}`
              : `Could not export purchase order PDF (HTTP ${response.status}).`
          );
          return;
        }
        const blob = await response.blob();
        const safe = po.purchaseOrderNumber.replace(/[^a-zA-Z0-9._-]+/g, '-').replace(/^-+|-+$/g, '') || 'export';
        const name =
          parseContentDispositionFilename(response.headers.get('Content-Disposition')) ?? `${safe}.pdf`;
        downloadBlob(blob, name);
        notifySuccess('Purchase order exported to PDF.');
      } catch {
        notifyError('Could not export purchase order PDF.');
      }
    },
    [auth?.token, navigate, notifySuccess, notifyError]
  );

  const selectedPo = useMemo(() => {
    if (!selectedPoId) {
      return null;
    }
    const fromList = purchaseOrdersList.find((p) => p.id === selectedPoId);
    if (fromList) {
      return fromList;
    }
    if (detailPoOverride?.id === selectedPoId) {
      return detailPoOverride;
    }
    return null;
  }, [purchaseOrdersList, selectedPoId, detailPoOverride]);

  const isProcurement = canViewProcurementModules(auth?.role ?? 'Requestor');
  const isPurchaseOrdersPath = path === '/purchase-orders' || path.startsWith('/purchase-orders/');
  const isRequestsPath = path === '/requests' || path.startsWith('/requests/');

  const showRequestsMain = isRequestsPath;

  const showPurchaseOrdersMain = isProcurement && isPurchaseOrdersPath;

  const isRfqPath = path === '/request-for-quotes' || path.startsWith('/request-for-quotes/');
  const sourcingEnabled = auth?.profile?.sourcingEnabled ?? true;
  const showRfqMain = isProcurement && isRfqPath && sourcingEnabled;
  const showWellsMain = isProcurement && path === '/wells';

  useEffect(() => {
    if (!auth?.profile) {
      return;
    }
    if (!isProcurement || !isRfqPath) {
      return;
    }
    if (auth.profile.sourcingEnabled === false) {
      navigate('/', { replace: true });
    }
  }, [auth?.profile, isProcurement, isRfqPath, navigate]);

  const isInventoryPath = path === '/inventory';
  const isMaterialTransferPath =
    path === '/material-transfers' ||
    path === '/material-transfers/new' ||
    path.startsWith('/material-transfers/');
  const materialTransfersEnabled = auth?.profile?.materialTransfersEnabled ?? true;
  useEffect(() => {
    if (!auth?.profile) {
      return;
    }
    if (materialTransfersEnabled) {
      return;
    }
    if (!isInventoryPath && !isMaterialTransferPath) {
      return;
    }
    navigate('/', { replace: true });
  }, [auth?.profile, isInventoryPath, isMaterialTransferPath, materialTransfersEnabled, navigate]);

  const showVendorsMain = isProcurement && path === '/vendors';

  const requestCounts = useMemo(
    () => requestCountsFromStatusMap(requestsStatusCounts),
    [requestsStatusCounts]
  );

  const poTileCounts = useMemo(() => poTileCountsFromMap(poStatusCounts), [poStatusCounts]);

  const rfqTileCounts = useMemo((): RfqTileCounts => {
    if (!auth?.role) {
      return emptyRfqTileCounts();
    }
    if (auth.role === 'OperationsManager') {
      return rfqTileCountsFromOperationsMap(rfqStatusCounts);
    }
    return rfqTileCountsFromProcurementMap(rfqStatusCounts);
  }, [auth?.role, rfqStatusCounts]);

  const fetchCurrentUserProfile = useCallback(async (token: string): Promise<CurrentUserProfile | null> => {
    try {
      const me = await fetch(AUTH_ME_URL, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (!me.ok) {
        return null;
      }
      return (await me.json()) as CurrentUserProfile;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    if (!auth?.token || auth.profile) {
      return;
    }
    let cancelled = false;
    void (async () => {
      const profile = await fetchCurrentUserProfile(auth.token);
      if (cancelled || !profile) {
        return;
      }
      const merged: LoginResponse = { ...auth, profile };
      writeStoredAuth(merged);
      setAuth(merged);
    })();
    return () => {
      cancelled = true;
    };
  }, [auth, fetchCurrentUserProfile]);

  const canApproveRfqWorkflow = useMemo(() => {
    const e = auth?.profile?.entitlements;
    if (e && typeof e.canApproveRfqWorkflow === 'boolean') {
      return e.canApproveRfqWorkflow;
    }
    return auth?.role === 'OperationsManager' || auth?.role === 'Administrator';
  }, [auth?.profile?.entitlements, auth?.role]);

  const authFetch = useCallback(
    async (url: string, init?: RequestInit): Promise<Response> => {
      const headers = new Headers(init?.headers ?? {});
      if (!(init?.body instanceof FormData)) {
        headers.set('Content-Type', 'application/json');
      }
      if (auth?.token) {
        headers.set('Authorization', `Bearer ${auth.token}`);
      }
      const response = await fetch(url, { ...init, headers });
      if (response.status === 401) {
        clearStoredAuth();
        setAuth(null);
        setError('Session expired. Please sign in again.');
        notifyError('Session expired. Please sign in again.');
        navigate('/', { replace: true });
      }
      return response;
    },
    [auth?.token, navigate, notifyError]
  );

  const loadRfqStatusCounts = useCallback(async (): Promise<void> => {
    if (!auth?.token || !showRfqMain) {
      return;
    }
    try {
      const url =
        auth.role === 'OperationsManager' ? OPERATIONS_RFQ_STATUS_COUNTS_URL : RFQ_STATUS_COUNTS_URL;
      const response = await authFetch(url);
      if (!response.ok) {
        setRfqStatusCounts({});
        return;
      }
      const raw = (await response.json()) as { statusCounts?: Record<string, unknown> };
      const sc = raw.statusCounts ?? {};
      const normalized: Record<string, number> = {};
      for (const [k, v] of Object.entries(sc)) {
        normalized[k] = typeof v === 'number' ? v : Number(v) || 0;
      }
      setRfqStatusCounts(normalized);
    } catch {
      setRfqStatusCounts({});
    }
  }, [auth?.token, auth?.role, authFetch, showRfqMain]);

  const loadRfqForRequest = useCallback(
    async (requestId: string): Promise<void> => {
      setRfqLoading(true);
      try {
        const u = new URL(RFQ_API_URL);
        u.searchParams.set('procurementRequestId', requestId);
        u.searchParams.set('page', '1');
        u.searchParams.set('pageSize', '50');
        const response = await authFetch(u.toString());
        if (!response.ok) {
          setRfqDetail(null);
          return;
        }
        const raw = (await response.json()) as { items: Record<string, unknown>[]; totalCount: number };
        const items = (raw.items ?? []).map((x) => mapApiRequestForQuote(x));
        const active = items
          .filter((r) => r.status.toLowerCase() !== 'cancelled')
          .sort((a, b) => new Date(b.createdAtUtc).getTime() - new Date(a.createdAtUtc).getTime());
        setRfqDetail(active[0] ?? null);
      } finally {
        setRfqLoading(false);
      }
    },
    [authFetch]
  );

  const applyRfqMutationResult = useCallback((next: RequestForQuoteDetail): void => {
    setRfqDetail((prev) => (prev?.id === next.id ? next : prev));
    setRegistryRfqDetail((prev) => (prev?.id === next.id ? next : prev));
  }, []);

  const loadRfqByIdForRegistry = useCallback(
    async (id: string): Promise<void> => {
      setRegistryRfqDetailLoading(true);
      try {
        const response = await authFetch(`${RFQ_API_URL}/${encodeURIComponent(id)}`);
        if (!response.ok) {
          setRegistryRfqDetail(null);
          return;
        }
        const raw = (await response.json()) as Record<string, unknown>;
        const detail = mapApiRequestForQuote(raw);
        setRegistryRfqDetail(detail);
        setRfqDetail((prev) => (prev?.id === detail.id ? detail : prev));
      } finally {
        setRegistryRfqDetailLoading(false);
      }
    },
    [authFetch]
  );

  const loadRfqRegistryList = useCallback(async (options?: { silent?: boolean }): Promise<void> => {
    if (!auth?.token || !showRfqMain) {
      return;
    }
    const silent = options?.silent === true;
    if (!silent) {
      setRfqListLoading(true);
    }
    try {
      if (auth.role === 'OperationsManager') {
        const params = new URLSearchParams();
        if (rfqStatusFilter === 'pendingApproval') {
          params.set('approvalFilter', 'pending');
        } else if (rfqStatusFilter === 'approved') {
          params.set('approvalFilter', 'approved');
        } else if (rfqStatusFilter === 'rejected') {
          params.set('approvalFilter', 'rejected');
        } else {
          params.set('approvalFilter', 'all');
        }
        const response = await authFetch(`${OPERATIONS_RFQ_API_URL}?${params.toString()}`);
        if (!response.ok) {
          setRfqListRows([]);
          setRfqListTotal(0);
          return;
        }
        const data = (await response.json()) as unknown;
        const list = Array.isArray(data) ? data : [];
        const details = list.map((x) => mapApiRequestForQuote(x as Record<string, unknown>));
        setRfqListRows(details.map(rfqDetailToSummary));
        setRfqListTotal(details.length);
        return;
      }

      const u = new URL(RFQ_API_URL);
      u.searchParams.set('page', String(rfqListPage));
      u.searchParams.set('pageSize', String(rfqListPageSize));
      if (rfqStatusFilter !== 'all') {
        u.searchParams.set('status', rfqStatusFilter);
      }
      const response = await authFetch(u.toString());
      if (!response.ok) {
        setRfqListRows([]);
        setRfqListTotal(0);
        return;
      }
      const raw = (await response.json()) as { items: Record<string, unknown>[]; totalCount: number };
      setRfqListRows((raw.items ?? []).map((x) => mapApiRfqSummary(x)));
      setRfqListTotal(Number(raw.totalCount ?? 0));
    } finally {
      if (!silent) {
        setRfqListLoading(false);
      }
    }
  }, [auth?.token, auth?.role, authFetch, rfqListPage, rfqListPageSize, rfqStatusFilter, showRfqMain]);

  const refreshRfqRegistry = useCallback(() => {
    void loadRfqRegistryList();
    void loadRfqStatusCounts();
  }, [loadRfqRegistryList, loadRfqStatusCounts]);

  const handleRfqListPageSizeChange = useCallback((pageSize: number): void => {
    setRfqListPageSize(pageSize);
    setRfqListPage(1);
  }, []);

  useEffect(() => {
    if (
      !effectiveRequestId ||
      !auth?.token ||
      !selectedRequest ||
      selectedRequest.id !== effectiveRequestId
    ) {
      setRfqDetail(null);
      return;
    }
    if (
      selectedRequest.status !== 'sourcing' ||
      !canPerformProcurementStaffActions(auth.role) ||
      !(auth?.profile?.sourcingEnabled ?? true)
    ) {
      setRfqDetail(null);
      return;
    }
    void loadRfqForRequest(effectiveRequestId);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- RFQ list depends on request id/status, not full object identity
  }, [effectiveRequestId, selectedRequest?.id, selectedRequest?.status, auth?.token, auth?.role, loadRfqForRequest]);

  useEffect(() => {
    if (routeRfqId) {
      setSelectedRfqRegistryId(routeRfqId);
    }
  }, [routeRfqId]);

  useEffect(() => {
    if (!showRfqMain || !auth?.token || !selectedRfqRegistryId) {
      if (!selectedRfqRegistryId) {
        setRegistryRfqDetail(null);
      }
      return;
    }
    void loadRfqByIdForRegistry(selectedRfqRegistryId);
  }, [showRfqMain, auth?.token, selectedRfqRegistryId, loadRfqByIdForRegistry]);

  useEffect(() => {
    if (!showRfqMain || !auth?.token) {
      return;
    }
    void loadRfqRegistryList();
    void loadRfqStatusCounts();
  }, [showRfqMain, auth?.token, loadRfqRegistryList, loadRfqStatusCounts]);

  useEffect(() => {
    setRfqListPage(1);
  }, [rfqStatusFilter]);

  useEffect(() => {
    if (!showRfqMain) {
      setSelectedRfqRegistryId(null);
      setRegistryRfqDetail(null);
      setRfqListRows([]);
      setRfqListTotal(0);
      setPoContextRequestForRfq(null);
      setRfqRegistryLinkedPo(null);
      setRfqStatusFilter('all');
      setRfqStatusCounts({});
    }
  }, [showRfqMain]);

  useEffect(() => {
    if (!registryRfqDetail || !auth?.token) {
      setPoContextRequestForRfq(null);
      return;
    }
    const st = normalizeRfqStatus(registryRfqDetail.status);
    if (st !== 'approved' && st !== 'cancelled' && st !== 'complete') {
      setPoContextRequestForRfq(null);
      return;
    }
    const requestId = registryRfqDetail.procurementRequestId;
    let cancelled = false;
    void (async () => {
      try {
        const response = await authFetch(`${API_BASE_URL}/${encodeURIComponent(requestId)}`);
        if (cancelled || !response.ok) {
          return;
        }
        const raw = (await response.json()) as Record<string, unknown>;
        if (!cancelled) {
          setPoContextRequestForRfq(normalizeProcurementRequest(raw));
        }
      } catch {
        if (!cancelled) {
          setPoContextRequestForRfq(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [registryRfqDetail, auth?.token, authFetch]);

  useEffect(() => {
    if (!showRfqMain || !registryRfqDetail || !auth?.token) {
      setRfqRegistryLinkedPo(null);
      return;
    }
    const st = normalizeRfqStatus(registryRfqDetail.status);
    const prId = registryRfqDetail.procurementRequestId?.trim();
    if (!prId) {
      setRfqRegistryLinkedPo(null);
      return;
    }
    // Load PO whenever this RFQ can have one: approved (PO may exist before RFQ is marked complete), cancelled escape hatch, or complete.
    if (st !== 'complete' && st !== 'approved' && st !== 'cancelled') {
      setRfqRegistryLinkedPo(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const response = await authFetch(poApiUrl(prId));
        if (cancelled) {
          return;
        }
        if (response.status === 404 || !response.ok) {
          setRfqRegistryLinkedPo(null);
          return;
        }
        setRfqRegistryLinkedPo(normalizePurchaseOrder((await response.json()) as PurchaseOrder));
      } catch {
        if (!cancelled) {
          setRfqRegistryLinkedPo(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showRfqMain, registryRfqDetail, auth?.token, authFetch]);

  useEffect(() => {
    if (!auth?.token) {
      setActiveRegions([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const response = await authFetch(REGIONS_LIST_URL);
        if (cancelled || !response.ok) {
          return;
        }
        const data = (await response.json()) as Record<string, unknown>[];
        if (Array.isArray(data)) {
          setActiveRegions(data.map((x) => mapRegionApiRow(x)));
        }
      } catch {
        /* non-fatal: dropdowns stay empty until retry */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [auth?.token, authFetch]);

  const refreshRequestInList = useCallback(
    async (requestId: string): Promise<void> => {
      if (!auth?.token) {
        return;
      }
      const response = await authFetch(`${API_BASE_URL}/${requestId}`);
      if (response.status === 401 || !response.ok) {
        return;
      }
      const raw = (await response.json()) as Record<string, unknown>;
      const updated = normalizeProcurementRequest(raw);
      setRequests((prev) => prev.map((r) => (r.id === requestId ? updated : r)));
    },
    [auth?.token, authFetch]
  );

  const loadWells = useCallback(async (): Promise<void> => {
    if (!auth?.token) {
      return;
    }
    try {
      const response = await authFetch(`${MASTER_WELLS_API_URL}?page=1&pageSize=100`);
      if (response.status === 401 || !response.ok) {
        return;
      }
      const data = (await response.json()) as { items?: Record<string, unknown>[] } | Record<string, unknown>[];
      const items = Array.isArray(data) ? data : data.items;
      setWells(Array.isArray(items) ? items.map((row) => mapMasterWellToPicker(row)) : []);
    } catch {
      setWells([]);
    }
  }, [auth?.token, authFetch]);

  const loadWellRegionCounts = useCallback(async (): Promise<void> => {
    if (!auth?.token || !canViewProcurementModules(auth.role)) {
      return;
    }
    try {
      const response = await authFetch(WELLS_REGION_COUNTS_URL);
      if (response.status === 401 || !response.ok) {
        return;
      }
      const data = (await response.json()) as { regions?: WellRegionCount[] };
      setWellRegionCounts(Array.isArray(data.regions) ? data.regions : []);
    } catch {
      setWellRegionCounts([]);
    }
  }, [auth?.token, auth?.role, authFetch]);

  const loadWellsPaged = useCallback(async (): Promise<void> => {
    if (!auth?.token || !canViewProcurementModules(auth.role)) {
      return;
    }
    try {
      setWellsPagedLoading(true);
      const params = new URLSearchParams();
      params.set('page', String(wellsPage));
      params.set('pageSize', String(wellsPageSize));
      if (wellRegionFilter) {
        params.set('region', wellRegionFilter);
      }
      const response = await authFetch(`${WELLS_PAGE_URL}?${params.toString()}`);
      if (response.status === 401 || !response.ok) {
        return;
      }
      const data = (await response.json()) as { items?: Record<string, unknown>[]; totalCount?: number };
      const items = Array.isArray(data.items) ? data.items.map((row) => mapApiWell(row)) : [];
      setWellsPaged(items);
      setWellsPagedTotal(typeof data.totalCount === 'number' ? data.totalCount : 0);
    } catch {
      setWellsPaged([]);
      setWellsPagedTotal(0);
    } finally {
      setWellsPagedLoading(false);
    }
  }, [auth?.token, auth?.role, authFetch, wellsPage, wellsPageSize, wellRegionFilter]);

  const loadVendorsPaged = useCallback(async (): Promise<void> => {
    if (!auth?.token || !canViewProcurementModules(auth.role)) {
      return;
    }
    try {
      setVendorsPagedLoading(true);
      const params = new URLSearchParams();
      params.set('page', String(vendorsPage));
      params.set('pageSize', String(vendorsPageSize));
      const response = await authFetch(`${VENDORS_PAGE_URL}?${params.toString()}`);
      if (response.status === 401 || !response.ok) {
        return;
      }
      const data = (await response.json()) as { items?: Record<string, unknown>[]; totalCount?: number };
      const items = Array.isArray(data.items) ? data.items.map((row) => mapApiVendor(row)) : [];
      setVendorsPaged(items);
      setVendorsPagedTotal(typeof data.totalCount === 'number' ? data.totalCount : 0);
    } catch {
      setVendorsPaged([]);
      setVendorsPagedTotal(0);
    } finally {
      setVendorsPagedLoading(false);
    }
  }, [auth?.token, auth?.role, authFetch, vendorsPage, vendorsPageSize]);

  const reloadWellsAfterMutation = useCallback(async (): Promise<void> => {
    await loadWells();
    if (auth?.token && canViewProcurementModules(auth.role) && path === '/wells') {
      void loadWellRegionCounts();
      void loadWellsPaged();
    }
  }, [loadWells, auth?.token, auth?.role, path, loadWellRegionCounts, loadWellsPaged]);

  const openNewRequestModal = useCallback((): void => {
    if (!auth || auth.role !== 'Requestor') {
      return;
    }
    setFormData(requestorFormWithUser(auth.displayName, auth.username));
    setNewRequestSubmitFieldErrors([]);
    void loadWells();
    setNewRequestModalOpen(true);
  }, [auth, loadWells]);

  const closeNewRequestModal = useCallback((): void => {
    setNewRequestModalOpen(false);
    setNewRequestSubmitFieldErrors([]);
  }, []);

  const loadRequests = useCallback(
    async (options?: { silent?: boolean }): Promise<void> => {
      if (!auth?.token) {
        return;
      }
      const silent = options?.silent === true;
      try {
        if (!silent) {
          setLoading(true);
        }
        setError('');
        const response = await authFetch(
          requestsListUrl(requestsPage, requestsPageSize, statusFilter, requestsSort.column, requestsSort.direction)
        );
        if (response.status === 401) {
          return;
        }
        if (!response.ok) {
          throw new Error('Unable to load requests');
        }
        const data = (await response.json()) as ProcurementRequestsPageDto;
        setRequests(data.items.map((item) => normalizeProcurementRequest(item as unknown as Record<string, unknown>)));
        setRequestsTotalCount(data.totalCount);
      } catch (err) {
        const msg = (err as Error).message;
        setError(msg);
        notifyError(msg);
      } finally {
        if (!silent) {
          setLoading(false);
        }
      }
    },
    [auth?.token, authFetch, notifyError, requestsPage, requestsPageSize, statusFilter, requestsSort.column, requestsSort.direction]
  );

  const loadRequestStatusCounts = useCallback(async (): Promise<void> => {
    if (!auth?.token) {
      return;
    }
    try {
      const response = await authFetch(REQUEST_STATUS_COUNTS_URL);
      if (response.status === 401) {
        return;
      }
      if (!response.ok) {
        throw new Error('Unable to load request status counts');
      }
      const data = (await response.json()) as ProcurementRequestStatusCountsDto;
      setRequestsStatusCounts(data.statusCounts ?? {});
    } catch (err) {
      const msg = (err as Error).message;
      setError(msg);
      notifyError(msg);
    }
  }, [auth?.token, authFetch, notifyError]);

  const loadPoStatusCounts = useCallback(async (): Promise<void> => {
    if (!auth?.token || !canViewProcurementModules(auth.role)) {
      return;
    }
    try {
      const response = await authFetch(PO_STATUS_COUNTS_URL);
      if (response.status === 401) {
        return;
      }
      if (!response.ok) {
        throw new Error('Unable to load purchase order status counts');
      }
      const data = (await response.json()) as PurchaseOrderStatusCountsDto;
      setPoStatusCounts(data.statusCounts ?? {});
    } catch (err) {
      const msg = (err as Error).message;
      setError(msg);
      notifyError(msg);
    }
  }, [auth?.token, auth?.role, authFetch, notifyError]);

  const loadVendors = useCallback(async (): Promise<void> => {
    if (!auth?.token || !canViewProcurementModules(auth.role)) {
      return;
    }
    try {
      const response = await authFetch(VENDORS_API_URL);
      if (response.status === 401 || !response.ok) {
        return;
      }
      const data = (await response.json()) as Record<string, unknown>[];
      setVendors(Array.isArray(data) ? data.map((row) => mapApiVendor(row)) : []);
    } catch {
      setVendors([]);
    }
  }, [auth?.token, auth?.role, authFetch]);

  const loadDeliveryAddresses = useCallback(async (): Promise<void> => {
    if (!auth?.token || !canLoadDeliveryAddressDirectory(auth.role)) {
      return;
    }
    try {
      const response = await authFetch(DELIVERY_ADDRESSES_API_URL);
      if (response.status === 401 || !response.ok) {
        return;
      }
      const data = (await response.json()) as Record<string, unknown>[];
      setDeliveryAddresses(Array.isArray(data) ? data.map((row) => mapApiDeliveryAddress(row)) : []);
    } catch {
      setDeliveryAddresses([]);
    }
  }, [auth?.token, auth?.role, authFetch]);

  const loadPurchaseOrdersList = useCallback(
    async (options?: { silent?: boolean }): Promise<void> => {
      if (!auth?.token || !canViewProcurementModules(auth.role)) {
        return;
      }
      const silent = options?.silent === true;
      try {
        if (!silent) {
          setPoRegistryLoading(true);
        }
        setError('');
        const response = await authFetch(purchaseOrdersListUrl(poPage, poPageSize, poStatusFilter));
        if (response.status === 401) {
          return;
        }
        if (!response.ok) {
          throw new Error('Unable to load purchase orders');
        }
        const data = (await response.json()) as PurchaseOrdersPageDto;
        setPurchaseOrdersList(data.items.map((p) => normalizePurchaseOrder(p)));
        setPoTotalCount(data.totalCount);
      } catch (err) {
        const msg = (err as Error).message;
        setError(msg);
        notifyError(msg);
      } finally {
        if (!silent) {
          setPoRegistryLoading(false);
        }
      }
    },
    [auth?.token, auth?.role, authFetch, notifyError, poPage, poPageSize, poStatusFilter]
  );

  useEffect(() => {
    setRequestsPage(1);
  }, [statusFilter, requestsSort.column]);

  useEffect(() => {
    setPoPage(1);
  }, [poStatusFilter]);

  useEffect(() => {
    void loadRequests();
  }, [loadRequests]);

  useEffect(() => {
    if (!effectiveRequestId || !auth?.token) {
      return;
    }
    void refreshRequestInList(effectiveRequestId);
  }, [effectiveRequestId, auth?.token, refreshRequestInList]);

  useEffect(() => {
    setDraftSubmitFieldErrors([]);
  }, [effectiveRequestId]);

  useEffect(() => {
    void loadRequestStatusCounts();
  }, [loadRequestStatusCounts]);

  useEffect(() => {
    if (!auth) {
      return;
    }
    if (auth.role === 'Requestor') {
      setFormData(requestorFormWithUser(auth.displayName, auth.username));
    } else {
      setFormData(emptyForm());
    }
  }, [auth]);

  useEffect(() => {
    if (!auth?.token) {
      return;
    }
    if (auth.role === 'Requestor' || canViewProcurementModules(auth.role)) {
      void loadWells();
    }
  }, [auth?.token, auth?.role, loadWells]);

  useEffect(() => {
    if (!auth?.token) {
      return;
    }
    if (canViewProcurementModules(auth.role)) {
      void loadVendors();
    }
    if (canLoadDeliveryAddressDirectory(auth.role)) {
      void loadDeliveryAddresses();
    }
  }, [auth?.token, auth?.role, loadVendors, loadDeliveryAddresses]);

  useEffect(() => {
    if (!showRfqMain || !auth?.token) {
      return;
    }
    void loadVendors();
  }, [showRfqMain, auth?.token, loadVendors]);

  useEffect(() => {
    if (!auth?.token) {
      return;
    }
    const pollHandle = window.setInterval(() => {
      void loadRequests({ silent: true });
      void loadRequestStatusCounts();
    }, UI_POLL_INTERVAL_MS);
    return () => {
      window.clearInterval(pollHandle);
    };
  }, [auth?.token, loadRequests, loadRequestStatusCounts]);

  useEffect(() => {
    if (!auth?.token) {
      hasHydratedRequestsRef.current = false;
      previousStatusByIdRef.current = {};
      return;
    }
    const currentStatusById: Record<string, RequestStatus> = {};
    requests.forEach((req) => {
      currentStatusById[req.id] = req.status;
    });
    if (!hasHydratedRequestsRef.current) {
      previousStatusByIdRef.current = currentStatusById;
      hasHydratedRequestsRef.current = true;
      return;
    }
    requests.forEach((req) => {
      const previousStatus = previousStatusByIdRef.current[req.id];
      if (!previousStatus || previousStatus === req.status) {
        return;
      }
      if (auth.role === 'RegionalManager' && req.status === 'pendingApproval') {
        notifyInfo(`New request ${req.requestId ?? req.id} submitted for your approval.`);
      }
      const isRequestorOwner =
        auth.role === 'Requestor' &&
        (req.requestor?.userId
          ? req.requestor.userId.toLowerCase() === auth.username.toLowerCase()
          : (req.requestor?.name ?? '').toLowerCase() === auth.displayName.toLowerCase());
      if ((isRequestorOwner || auth.role === 'RegionalManager') && req.status === 'sourcing') {
        notifyInfo(`Request ${req.requestId ?? req.id} moved to sourcing.`);
      }
    });
    previousStatusByIdRef.current = currentStatusById;
  }, [requests, auth, notifyInfo]);

  useEffect(() => {
    if (!auth) {
      setStatusFilter('all');
      return;
    }
    if (auth.role === 'Requestor') {
      setStatusFilter('draft');
    } else if (auth.role === 'RegionalManager') {
      setStatusFilter('pendingApproval');
    } else if (auth.role === 'ITSupport' || auth.role === 'Administrator') {
      setStatusFilter('all');
    } else if (canViewProcurementModules(auth.role)) {
      setStatusFilter('procurementReview');
    } else {
      setStatusFilter('all');
    }
  }, [auth]);

  useEffect(() => {
    if (auth?.role === 'RegionalManager' && statusFilter === 'draft') {
      setStatusFilter('pendingApproval');
    }
  }, [auth?.role, statusFilter]);

  useEffect(() => {
    if (!isPurchaseOrdersPath || !auth?.token || !canViewProcurementModules(auth.role)) {
      return;
    }
    void loadPurchaseOrdersList();
    void loadPoStatusCounts();
  }, [isPurchaseOrdersPath, auth?.token, auth?.role, loadPurchaseOrdersList, loadPoStatusCounts]);

  useEffect(() => {
    if (!auth?.token || !canViewProcurementModules(auth.role) || !isPurchaseOrdersPath) {
      return;
    }
    const pollPo = window.setInterval(() => {
      void loadPurchaseOrdersList({ silent: true });
      void loadPoStatusCounts();
    }, UI_POLL_INTERVAL_MS);
    return () => {
      window.clearInterval(pollPo);
    };
  }, [auth?.token, auth?.role, isPurchaseOrdersPath, loadPurchaseOrdersList, loadPoStatusCounts]);

  useEffect(() => {
    if (!auth?.token || !showRfqMain) {
      return;
    }
    const pollRfq = window.setInterval(() => {
      void loadRfqRegistryList({ silent: true });
      void loadRfqStatusCounts();
    }, UI_POLL_INTERVAL_MS);
    return () => {
      window.clearInterval(pollRfq);
    };
  }, [auth?.token, showRfqMain, loadRfqRegistryList, loadRfqStatusCounts]);

  useEffect(() => {
    if (!auth?.token || !canViewProcurementModules(auth.role) || path !== '/wells') {
      return;
    }
    void loadWellRegionCounts();
    void loadWellsPaged();
  }, [auth?.token, auth?.role, path, loadWellRegionCounts, loadWellsPaged]);

  useEffect(() => {
    if (!auth?.token || !canViewProcurementModules(auth.role) || path !== '/vendors') {
      return;
    }
    void loadVendorsPaged();
  }, [auth?.token, auth?.role, path, loadVendorsPaged]);

  useEffect(() => {
    if (!effectiveRequestId || !auth?.token) {
      setPurchaseOrder(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      const response = await authFetch(poApiUrl(effectiveRequestId));
      if (cancelled) {
        return;
      }
      if (response.status === 404) {
        setPurchaseOrder(null);
        return;
      }
      if (!response.ok) {
        setPurchaseOrder(null);
        return;
      }
      setPurchaseOrder(normalizePurchaseOrder((await response.json()) as PurchaseOrder));
    })();
    return () => {
      cancelled = true;
    };
  }, [effectiveRequestId, auth?.token, authFetch]);

  useEffect(() => {
    const id = effectiveRequestId;
    if (!id || !auth?.token) {
      setDetailRequestOverride(null);
      setRequestDetailNotFound(false);
      return;
    }
    if (requests.some((r) => r.id === id)) {
      setDetailRequestOverride(null);
      setRequestDetailNotFound(false);
      return;
    }
    let cancelled = false;
    setRequestDetailNotFound(false);
    void (async () => {
      const response = await authFetch(`${API_BASE_URL}/${id}`);
      if (cancelled || response.status === 401) {
        return;
      }
      if (!response.ok) {
        if (!cancelled) {
          setDetailRequestOverride(null);
          setRequestDetailNotFound(true);
        }
        return;
      }
      const raw = (await response.json()) as Record<string, unknown>;
      if (!cancelled) {
        setRequestDetailNotFound(false);
        setDetailRequestOverride(normalizeProcurementRequest(raw));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [effectiveRequestId, requests, auth?.token, authFetch]);

  useEffect(() => {
    if (!selectedPoId || !auth?.token || !canViewProcurementModules(auth.role)) {
      setDetailPoOverride(null);
      return;
    }
    if (purchaseOrdersList.some((p) => p.id === selectedPoId)) {
      setDetailPoOverride(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      const response = await authFetch(`${PO_REGISTRY_URL}/${selectedPoId}`);
      if (cancelled || response.status === 401) {
        return;
      }
      if (!response.ok) {
        if (!cancelled) {
          setDetailPoOverride(null);
        }
        return;
      }
      const raw = (await response.json()) as PurchaseOrder;
      if (!cancelled) {
        setDetailPoOverride(normalizePurchaseOrder(raw));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedPoId, purchaseOrdersList, auth?.token, auth?.role, authFetch]);

  useEffect(() => {
    setPanelComment('');
  }, [effectiveRequestId]);

  /** Only when the selected request identity changes — not on every list poll (same id) — so draft edits are not reset. */
  useEffect(() => {
    if (!selectedRequest) {
      return;
    }
    if (selectedRequest.status !== 'draft' && selectedRequest.status !== 'rejected') {
      return;
    }
    setDraftEditForm(requestToFormState(selectedRequest));
    // Intentionally omit selectedRequest from deps: list refresh replaces object reference and would wipe in-progress draft edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRequest?.id, selectedRequest?.status]);

  async function login(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError('');
    setMessage('');
    const response = await fetch(`${AUTH_BASE_URL}/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username: loginUsername, password: loginPassword })
    });
    if (!response.ok) {
      setError('Invalid username or password.');
      notifyError('Invalid username or password.');
      return;
    }
    const loginData = (await response.json()) as LoginResponse;
    const profile = await fetchCurrentUserProfile(loginData.token);
    const session: LoginResponse = profile
      ? { ...loginData, profile, authKind: 'local' }
      : { ...loginData, authKind: 'local' };
    writeStoredAuth(session);
    setAuth(session);
  }

  async function loginWithEntra(): Promise<void> {
    setError('');
    setMessage('');
    try {
      const result = await acquireEntraApiAccessToken();
      const profile = await fetchCurrentUserProfile(result.accessToken);
      if (!profile) {
        setError('Could not load your profile from the API. Check API URL and Entra settings.');
        notifyError('Could not load your profile from the API.');
        return;
      }
      const session: LoginResponse = {
        token: result.accessToken,
        username: profile.userId,
        displayName: profile.displayName,
        role: profile.role,
        profile,
        authKind: 'entra'
      };
      writeStoredAuth(session);
      setAuth(session);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Microsoft sign-in failed.';
      setError(msg);
      notifyError(msg);
    }
  }

  async function submitRequest(event?: FormEvent<HTMLFormElement>): Promise<void> {
    event?.preventDefault();
    setMessage('');
    setError('');
    if (!auth) {
      return;
    }
    const v = validateSubmitForm(formData);
    const wErr = validateWellSelectedFromDirectory(formData, directoryWellKeys);
    if (v || wErr) {
      const keys = getSubmitValidationFieldErrors(formData);
      if (wErr) {
        keys.push(SUBMIT_FIELD_WELL_DIRECTORY);
      }
      setNewRequestSubmitFieldErrors(Array.from(new Set(keys)));
      const msg = v ?? wErr ?? 'Fix the highlighted fields.';
      setError(msg);
      notifyError(msg);
      return;
    }
    setNewRequestSubmitFieldErrors([]);
    const response = await authFetch(API_BASE_URL, {
      method: 'POST',
      body: JSON.stringify(buildCreateApiPayload(formData, false))
    });
    if (!response.ok) {
      setError('Could not submit request.');
      notifyError('Could not submit request.');
      return;
    }
    setFormData(requestorFormWithUser(auth.displayName, auth.username));
    setNewRequestModalOpen(false);
    setNewRequestSubmitFieldErrors([]);
    setMessage('Request submitted for regional manager approval.');
    notifySuccess('Request submitted successfully.');
    notifyInfo('Notification sent to Regional Manager.');
    await loadRequests();
    void loadRequestStatusCounts();
  }

  async function saveDraft(): Promise<void> {
    setMessage('');
    setError('');
    const d = validateDraftSave(formData);
    if (d) {
      setError(d);
      notifyError(d);
      return;
    }
    const response = await authFetch(API_BASE_URL, {
      method: 'POST',
      body: JSON.stringify(buildCreateApiPayload(formData, true))
    });
    if (!response.ok) {
      setError('Could not save draft.');
      notifyError('Could not save draft.');
      return;
    }
    setNewRequestModalOpen(false);
    setNewRequestSubmitFieldErrors([]);
    setMessage('Draft saved.');
    notifySuccess('Draft saved.');
    await loadRequests();
    void loadRequestStatusCounts();
  }

  async function updateSelectedDraft(): Promise<void> {
    if (!selectedRequest || !auth) {
      return;
    }
    if (selectedRequest.status !== 'draft' && selectedRequest.status !== 'rejected') {
      return;
    }
    const d = validateDraftSave(draftEditForm);
    if (d) {
      setError(d);
      notifyError(d);
      return;
    }
    setMessage('');
    setError('');
    const response = await authFetch(`${API_BASE_URL}/${selectedRequest.id}/draft`, {
      method: 'PUT',
      body: JSON.stringify(buildUpdateDraftApiPayload(draftEditForm))
    });
    if (!response.ok) {
      setError('Could not update request.');
      notifyError('Could not update request.');
      return;
    }
    const msg = selectedRequest.status === 'rejected' ? 'Request updated.' : 'Draft updated.';
    setMessage(msg);
    notifySuccess(msg);
    await loadRequests();
    void loadRequestStatusCounts();
  }

  async function deleteSelectedDraft(): Promise<void> {
    if (!selectedRequest) {
      return;
    }
    if (selectedRequest.status !== 'draft') {
      return;
    }
    setMessage('');
    setError('');
    const response = await authFetch(`${API_BASE_URL}/${selectedRequest.id}/draft`, {
      method: 'DELETE'
    });
    if (!response.ok) {
      setError('Could not delete draft.');
      notifyError('Could not delete draft.');
      return;
    }
    setMessage('Draft deleted.');
    notifySuccess('Draft deleted.');
    setSelectedId(null);
    await loadRequests();
    void loadRequestStatusCounts();
  }

  async function submitSelectedDraftForApproval(): Promise<void> {
    if (!selectedRequest) {
      return;
    }
    setMessage('');
    setError('');
    const v = validateSubmitForm(draftEditForm);
    const wErr = validateWellSelectedFromDirectory(draftEditForm, directoryWellKeys);
    if (v || wErr) {
      const keys = getSubmitValidationFieldErrors(draftEditForm);
      if (wErr) {
        keys.push(SUBMIT_FIELD_WELL_DIRECTORY);
      }
      setDraftSubmitFieldErrors(Array.from(new Set(keys)));
      const msg = v ?? wErr ?? 'Fix the highlighted fields.';
      setError(msg);
      notifyError(msg);
      return;
    }
    setDraftSubmitFieldErrors([]);
    // Persist editor state first: submit-draft validates the document in the database, not React state.
    const putResponse = await authFetch(`${API_BASE_URL}/${selectedRequest.id}/draft`, {
      method: 'PUT',
      body: JSON.stringify(buildUpdateDraftApiPayload(draftEditForm))
    });
    if (!putResponse.ok) {
      setError('Could not save your changes before submit. Fix any issues and try again.');
      notifyError('Could not save your changes before submit.');
      return;
    }
    const response = await authFetch(`${API_BASE_URL}/${selectedRequest.id}/submit-draft`, {
      method: 'POST'
    });
    if (!response.ok) {
      setError('Could not submit draft for approval. Ensure all fields are complete.');
      notifyError('Could not submit draft for approval. Ensure all fields are complete.');
      return;
    }
    const wasRejected = selectedRequest.status === 'rejected';
    const msg = wasRejected ? 'Request resubmitted for regional approval.' : 'Draft submitted for approval.';
    setMessage(msg);
    notifySuccess(msg);
    notifyInfo('Notification sent to Regional Manager.');
    await loadRequests();
    void loadRequestStatusCounts();
  }

  async function applyRegionalDecision(id: string, approve: boolean): Promise<void> {
    setMessage('');
    setError('');
    const response = await authFetch(`${API_BASE_URL}/${id}/regional-decision`, {
      method: 'POST',
      body: JSON.stringify({
        regionalManagerName: auth?.displayName ?? 'Regional Manager',
        approve,
        comment:
          panelComment.trim() || (approve ? 'Approved for procurement review.' : 'Rejected by regional manager.')
      })
    });
    if (!response.ok) {
      setError('Regional decision failed.');
      notifyError('Regional decision failed.');
      return;
    }
    setMessage(approve ? 'Request approved and sent to procurement review.' : 'Request rejected.');
    notifySuccess(approve ? 'Request approved and sent to procurement review.' : 'Request rejected.');
    setPanelComment('');
    await loadRequests();
    void loadRequestStatusCounts();
  }

  async function queueForProcurement(id: string): Promise<void> {
    setMessage('');
    setError('');
    const response = await authFetch(`${API_BASE_URL}/${id}/queue-procurement`, {
      method: 'POST',
      body: JSON.stringify({ procurementManagerName: auth?.displayName ?? 'Procurement Manager' })
    });
    if (!response.ok) {
      setError('Could not move request to sourcing.');
      notifyError('Could not move request to sourcing.');
      return;
    }
    setMessage('Request moved to sourcing.');
    notifySuccess('Request moved to sourcing.');
    notifyInfo('Notifications sent to Requestor and Regional Manager.');
    await loadRequests();
    void loadRequestStatusCounts();
  }

  async function createRfq(): Promise<void> {
    if (!selectedRequest) {
      return;
    }
    setMessage('');
    setError('');
    const response = await authFetch(RFQ_API_URL, {
      method: 'POST',
      body: JSON.stringify({ procurementRequestId: selectedRequest.id })
    });
    if (!response.ok) {
      const text = await response.text();
      setError(text || 'Could not create request for quote.');
      notifyError(text || 'Could not create request for quote.');
      return;
    }
    const raw = (await response.json()) as Record<string, unknown>;
    const created = mapApiRequestForQuote(raw);
    setRfqDetail(created);
    setRegistryRfqDetail((prev) => (prev?.id === created.id ? created : prev));
    setMessage('Request for quote created.');
    notifySuccess('Request for quote created.');
    void refreshRfqRegistry();
  }

  async function updateRfq(
    id: string,
    payload: { requirements: string; incotermsSnapshot: string; vendorIds: string[] }
  ): Promise<void> {
    const response = await authFetch(`${RFQ_API_URL}/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify({
        requirements: payload.requirements,
        incotermsSnapshot: payload.incotermsSnapshot,
        vendorIds: payload.vendorIds
      })
    });
    if (!response.ok) {
      const text = await response.text();
      notifyError(text || 'Could not save quote request.');
      return;
    }
    const raw = (await response.json()) as Record<string, unknown>;
    applyRfqMutationResult(mapApiRequestForQuote(raw));
    notifySuccess('Quote request saved.');
    void refreshRfqRegistry();
  }

  async function sendRfqInvitations(id: string): Promise<void> {
    const response = await authFetch(`${RFQ_API_URL}/${encodeURIComponent(id)}/send`, { method: 'POST' });
    if (!response.ok) {
      const text = await response.text();
      notifyError(text || 'Could not send invitations.');
      return;
    }
    const raw = (await response.json()) as Record<string, unknown>;
    applyRfqMutationResult(mapApiRequestForQuote(raw));
    notifySuccess('Invitations sent to vendors.');
    void refreshRfqRegistry();
  }

  async function selectVendorForRfq(id: string, vendorId: string): Promise<void> {
    const response = await authFetch(`${RFQ_API_URL}/${encodeURIComponent(id)}/select-vendor`, {
      method: 'POST',
      body: JSON.stringify({ vendorId })
    });
    if (!response.ok) {
      const text = await response.text();
      notifyError(text || 'Could not submit for approval.');
      return;
    }
    const raw = (await response.json()) as Record<string, unknown>;
    applyRfqMutationResult(mapApiRequestForQuote(raw));
    notifySuccess('Submitted for operations approval.');
    void refreshRfqRegistry();
  }

  async function cancelRfq(id: string): Promise<void> {
    const response = await authFetch(`${RFQ_API_URL}/${encodeURIComponent(id)}/cancel`, { method: 'POST' });
    if (!response.ok) {
      const text = await response.text();
      notifyError(text || 'Could not cancel quote request.');
      return;
    }
    const raw = (await response.json()) as Record<string, unknown>;
    applyRfqMutationResult(mapApiRequestForQuote(raw));
    notifySuccess('Quote request cancelled. You can create a purchase order if policy allows.');
    void refreshRfqRegistry();
  }

  async function approveRfq(id: string, comment?: string): Promise<void> {
    const response = await authFetch(`${OPERATIONS_RFQ_API_URL}/${encodeURIComponent(id)}/approve`, {
      method: 'POST',
      body: JSON.stringify({ comment: comment?.trim() ? comment.trim() : null })
    });
    if (!response.ok) {
      const text = await response.text();
      notifyError(text || 'Could not approve quote request.');
      return;
    }
    const raw = (await response.json()) as Record<string, unknown>;
    applyRfqMutationResult(mapApiRequestForQuote(raw));
    notifySuccess('Quote request approved.');
    void refreshRfqRegistry();
  }

  async function rejectRfq(id: string, comment?: string): Promise<void> {
    const response = await authFetch(`${OPERATIONS_RFQ_API_URL}/${encodeURIComponent(id)}/reject`, {
      method: 'POST',
      body: JSON.stringify({ comment: comment?.trim() ? comment.trim() : null })
    });
    if (!response.ok) {
      const text = await response.text();
      notifyError(text || 'Could not reject quote request.');
      return;
    }
    const raw = (await response.json()) as Record<string, unknown>;
    applyRfqMutationResult(mapApiRequestForQuote(raw));
    notifySuccess('Quote request rejected. Procurement can revise vendors and resubmit.');
    void refreshRfqRegistry();
  }

  async function createPurchaseOrder(forProcurementRequestId?: string): Promise<void> {
    if (!auth) {
      return;
    }
    const requestId = forProcurementRequestId ?? selectedRequest?.id;
    if (!requestId) {
      return;
    }
    setMessage('');
    setError('');
    let req: ProcurementRequest | null = selectedRequest?.id === requestId ? selectedRequest : null;
    if (!req && poContextRequestForRfq?.id === requestId) {
      req = poContextRequestForRfq;
    }
    if (!req) {
      const loadResponse = await authFetch(`${API_BASE_URL}/${encodeURIComponent(requestId)}`);
      if (!loadResponse.ok) {
        const text = await loadResponse.text();
        setError(text || 'Could not load request for purchase order.');
        notifyError(text || 'Could not load request for purchase order.');
        return;
      }
      req = normalizeProcurementRequest((await loadResponse.json()) as Record<string, unknown>);
    }
    const unitCost = Number(poForm.unitCost);
    if (
      !poForm.vendorId.trim() ||
      !poForm.deliveryAddressId.trim() ||
      !poForm.incoterms.trim() ||
      Number.isNaN(unitCost) ||
      unitCost < 0
    ) {
      setError(
        'Select a vendor and a delivery address, fill Incoterms, and enter a valid unit cost.'
      );
      notifyError(
        'Select a vendor and a delivery address, fill Incoterms, and enter a valid unit cost.'
      );
      return;
    }
    const mat = primaryMaterialLine(req);
    if (!mat) {
      setError('This request has no material lines to place on a purchase order.');
      notifyError('This request has no material lines to place on a purchase order.');
      return;
    }
    const need = materialInventoryNeed(mat);
    const poQuantity = need.trackFootage
      ? mat.quantity
      : Math.max(0, Math.round(need.remaining));
    if (!need.trackFootage && poQuantity <= 0) {
      setError('Yard stock already covers this line. A purchase order is not needed.');
      notifyError('Yard stock already covers this line. A purchase order is not needed.');
      return;
    }
    const freightParsed = parseOptionalUsdAmount(poForm.freightAmount);
    const miscParsed = parseOptionalUsdAmount(poForm.miscellaneousFeeAmount);
    if (!freightParsed.ok || !miscParsed.ok) {
      let msg = 'Invalid amount.';
      if (!freightParsed.ok) {
        msg = freightParsed.message;
      } else if (!miscParsed.ok) {
        msg = miscParsed.message;
      }
      setError(msg);
      notifyError(msg);
      return;
    }
    const response = await authFetch(poApiUrl(req.id), {
      method: 'POST',
      body: JSON.stringify({
        vendorId: poForm.vendorId.trim(),
        vendorName: null,
        vendorAddress: null,
        deliveryAddressId: poForm.deliveryAddressId.trim(),
        deliveryAddress: null,
        incoterms: poForm.incoterms.trim(),
        freightAmount: freightParsed.amount,
        miscellaneousFeeAmount: miscParsed.amount,
        lineItems: [
          {
            partNumber: (mat.name || mat.itemId).trim(),
            equipmentType: mat.name.trim(),
            quantity: poQuantity,
            unitCost,
            description: poForm.lineDescription.trim() || mat.description || undefined
          }
        ]
      })
    });
    if (response.status === 409) {
      setError('A purchase order already exists for this request.');
      notifyError('A purchase order already exists for this request.');
      return;
    }
    if (!response.ok) {
      const text = await response.text();
      setError(text || 'Could not create purchase order.');
      notifyError(text || 'Could not create purchase order.');
      return;
    }
    const createdPo = normalizePurchaseOrder((await response.json()) as PurchaseOrder);
    setPurchaseOrder(createdPo);
    const activeRfqContextId = routeRfqId ?? selectedRfqRegistryId;
    if (activeRfqContextId) {
      setRfqRegistryLinkedPo(createdPo);
      void loadRfqByIdForRegistry(activeRfqContextId);
    }
    setMessage('Purchase order created. Request is now order placed.');
    notifySuccess('Purchase order created. Request is now order placed.');
    await loadRequests();
    void loadRequestStatusCounts();
    void loadPurchaseOrdersList();
    void loadPoStatusCounts();
    void refreshRfqRegistry();
  }

  async function updatePurchaseOrder(id: string, payload: UpdatePurchaseOrderPayload): Promise<void> {
    setMessage('');
    setError('');
    const response = await authFetch(`${PO_REGISTRY_URL}/${encodeURIComponent(id)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        freightAmount: payload.freightAmount,
        miscellaneousFeeAmount: payload.miscellaneousFeeAmount,
        lineItemUnitCosts: payload.lineItemUnitCosts
      })
    });
    if (!response.ok) {
      const text = await response.text();
      setError(text || 'Could not update purchase order.');
      notifyError(text || 'Could not update purchase order.');
      return;
    }
    const updated = normalizePurchaseOrder((await response.json()) as PurchaseOrder);
    setMessage(`Purchase order ${updated.purchaseOrderNumber} updated.`);
    notifySuccess('Purchase order pricing saved.');
    if (effectiveRequestId === updated.procurementRequestId) {
      setPurchaseOrder(updated);
    }
    if (selectedPoId === updated.id) {
      setDetailPoOverride(updated);
    }
    await loadPurchaseOrdersList();
    void loadPoStatusCounts();
  }

  async function markPoPaid(id: string): Promise<void> {
    setMessage('');
    setError('');
    const response = await authFetch(`${PO_REGISTRY_URL}/${id}/mark-paid`, {
      method: 'POST'
    });
    if (!response.ok) {
      setError('Could not mark purchase order as paid.');
      notifyError('Could not mark purchase order as paid.');
      return;
    }
    const updated = normalizePurchaseOrder((await response.json()) as PurchaseOrder);
    setMessage(`Purchase order ${updated.purchaseOrderNumber} marked paid.`);
    notifySuccess(`Purchase order ${updated.purchaseOrderNumber} marked paid.`);
    if (effectiveRequestId === updated.procurementRequestId) {
      setPurchaseOrder(updated);
    }
    await loadPurchaseOrdersList();
    void loadPoStatusCounts();
  }

  async function emailVendorToPo(po: PurchaseOrder): Promise<void> {
    setMessage('');
    setError('');
    const response = await authFetch(`${PO_REGISTRY_URL}/${encodeURIComponent(po.id)}/email-vendor`, {
      method: 'POST'
    });
    if (!response.ok) {
      const text = await response.text();
      const msg = text.trim() || 'Could not queue email to vendor.';
      setError(msg);
      notifyError(msg);
      return;
    }
    setMessage(`Purchase order ${po.purchaseOrderNumber} — vendor email queued.`);
    notifySuccess(`Email to vendor queued (PDF for ${po.purchaseOrderNumber} will be sent shortly).`);
  }

  async function markProcured(id: string): Promise<void> {
    setMessage('');
    setError('');
    const response = await authFetch(`${API_BASE_URL}/${id}/mark-procured`, {
      method: 'POST'
    });
    if (!response.ok) {
      setError('Could not mark request as procured.');
      notifyError('Could not mark request as procured.');
      return;
    }
    setMessage('Request marked procured.');
    notifySuccess('Request marked procured.');
    await loadRequests();
    void loadRequestStatusCounts();
  }

  async function rejectProcurementRequest(id: string, comment?: string): Promise<void> {
    setMessage('');
    setError('');
    const response = await authFetch(`${API_BASE_URL}/${id}/reject-procurement`, {
      method: 'POST',
      body: JSON.stringify({ comment: comment?.trim() ? comment.trim() : null })
    });
    if (!response.ok) {
      const text = await response.text();
      setError(text || 'Could not reject request.');
      notifyError(text || 'Could not reject request.');
      return;
    }
    setMessage('Request returned to requestor.');
    notifySuccess('Request rejected and returned to the requestor.');
    await loadRequests();
    void loadRequestStatusCounts();
  }

  async function markCompleteFinal(id: string): Promise<void> {
    setMessage('');
    setError('');
    const response = await authFetch(`${API_BASE_URL}/${id}/mark-complete`, {
      method: 'POST'
    });
    if (!response.ok) {
      setError('Could not mark request complete.');
      notifyError('Could not mark request complete.');
      return;
    }
    setMessage('Request marked complete.');
    notifySuccess('Request marked complete.');
    await loadRequests();
    void loadRequestStatusCounts();
  }

  function closePanel(): void {
    setSelectedId(null);
  }

  function closePoPanel(): void {
    setSelectedPoId(null);
  }

  async function addRequestNote(body: string): Promise<void> {
    if (!selectedRequest || !auth?.token) {
      return;
    }
    setMessage('');
    setError('');
    const response = await authFetch(`${API_BASE_URL}/${selectedRequest.id}/notes`, {
      method: 'POST',
      body: JSON.stringify({ body })
    });
    if (response.status === 403) {
      setError('You do not have permission to add notes on this request.');
      notifyError('You do not have permission to add notes on this request.');
      return;
    }
    if (!response.ok) {
      const text = await response.text();
      setError(text || 'Could not save note.');
      notifyError(text || 'Could not save note.');
      return;
    }
    const raw = (await response.json()) as Record<string, unknown>;
    const updated = normalizeProcurementRequest(raw);
    setRequests((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
    notifySuccess('Note added.');
  }

  const canManageSelectedDraft =
    !!auth &&
    auth.role === 'Requestor' &&
    (selectedRequest?.status === 'draft' || selectedRequest?.status === 'rejected') &&
    (selectedRequest.requestor?.userId
      ? selectedRequest.requestor.userId.toLowerCase() === auth.username.toLowerCase()
      : (selectedRequest.requestor?.name ?? '').toLowerCase() === auth.displayName.toLowerCase());

  async function handleLogout(): Promise<void> {
    if (auth?.authKind === 'entra' && isEntraLoginAvailable()) {
      await signOutEntraPopup();
    }
    clearStoredAuth();
    setAuth(null);
    setRequests([]);
    setSelectedId(null);
    setPurchaseOrdersList([]);
    setSelectedPoId(null);
    setMessage('');
    setFormData(emptyForm());
    setStatusFilter('all');
    setRequestsSort({ column: null, direction: null });
    setRequestsPage(1);
    setPoPage(1);
    setRequestsTotalCount(0);
    setPoTotalCount(0);
    setRequestsStatusCounts({});
    setPoStatusFilter('all');
    setPoStatusCounts({});
    setWells([]);
    setWellsPaged([]);
    setWellsPagedTotal(0);
    setWellsPage(1);
    setWellRegionFilter(null);
    setWellRegionCounts([]);
    setVendorsPaged([]);
    setVendorsPagedTotal(0);
    setVendorsPage(1);
    setNewRequestModalOpen(false);
    setDetailRequestOverride(null);
    setRequestDetailNotFound(false);
    setDetailPoOverride(null);
    setRfqDetail(null);
    setRegistryRfqDetail(null);
    setRegistryRfqDetailLoading(false);
    setRfqListRows([]);
    setRfqListLoading(false);
    setRfqListPage(1);
    setRfqListTotal(0);
    setSelectedRfqRegistryId(null);
    setPoContextRequestForRfq(null);
    setRfqStatusFilter('all');
    setRfqStatusCounts({});
    setActiveRegions([]);
    navigate('/', { replace: true });
  }

  if (path === '/vendor-quote') {
    return (
      <div className="app-shell">
        <div className="app-content">
          <VendorQuotePage />
        </div>
        <AppFooter />
        <ToastContainer position="top-right" autoClose={4500} newestOnTop closeOnClick theme="colored" />
      </div>
    );
  }

  if (!auth) {
    return (
      <div className="app-shell">
        <LoginPage
          error={error}
          loginUsername={loginUsername}
          setLoginUsername={setLoginUsername}
          loginPassword={loginPassword}
          setLoginPassword={setLoginPassword}
          onSubmit={login}
          onEntraSignIn={isEntraLoginAvailable() ? loginWithEntra : undefined}
        />
        <AppFooter />
        <ToastContainer position="top-right" autoClose={3500} newestOnTop closeOnClick theme="colored" />
      </div>
    );
  }

  return (
    <div className="app-shell">
      <AppAuthenticatedRoutes
        auth={auth}
        onLogout={handleLogout}
        loading={loading}
        poRegistryLoading={poRegistryLoading}
        error={error}
        message={message}
        showRequestsMain={showRequestsMain}
        showPurchaseOrdersMain={showPurchaseOrdersMain}
        showWellsMain={showWellsMain}
        showVendorsMain={showVendorsMain}
        showRfqMain={showRfqMain}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        requestCounts={requestCounts}
        displayedRequests={requests}
        requestsPage={requestsPage}
        requestsPageSize={requestsPageSize}
        requestsTotalCount={requestsTotalCount}
        onRequestsPageChange={setRequestsPage}
        onRequestsPageSizeChange={handleRequestsPageSizeChange}
        requestsSortColumn={requestsSort.column}
        requestsSortDirection={requestsSort.direction}
        onRequestsSortClick={handleRequestsSortClick}
        selectedId={effectiveRequestId}
        setSelectedId={setSelectedId}
        requestDetailNotFound={requestDetailNotFound}
        purchaseOrdersList={purchaseOrdersList}
        poPage={poPage}
        poPageSize={poPageSize}
        poTotalCount={poTotalCount}
        onPoPageChange={setPoPage}
        onPoPageSizeChange={handlePoPageSizeChange}
        selectedPoId={selectedPoId}
        setSelectedPoId={setSelectedPoId}
        selectedRequest={selectedRequest}
        selectedPo={selectedPo}
        formData={formData}
        setFormData={setFormData}
        draftEditForm={draftEditForm}
        setDraftEditForm={setDraftEditForm}
        panelComment={panelComment}
        setPanelComment={setPanelComment}
        onAddRequestNote={addRequestNote}
        purchaseOrder={purchaseOrder}
        poForm={poForm}
        setPoForm={setPoForm}
        vendors={vendors}
        onReloadVendors={loadVendors}
        deliveryAddresses={deliveryAddresses}
        canManageSelectedDraft={canManageSelectedDraft}
        poStatusFilter={poStatusFilter}
        setPoStatusFilter={setPoStatusFilter}
        poTileCounts={poTileCounts}
        rfqStatusFilter={rfqStatusFilter}
        setRfqStatusFilter={setRfqStatusFilter}
        rfqTileCounts={rfqTileCounts}
        onSubmitNewRequest={submitRequest}
        onSaveDraft={saveDraft}
        wells={wells}
        onReloadWells={reloadWellsAfterMutation}
        wellsPaged={wellsPaged}
        wellsPagedTotal={wellsPagedTotal}
        wellsPage={wellsPage}
        wellsPageSize={wellsPageSize}
        onWellsPageChange={setWellsPage}
        onWellsPageSizeChange={handleWellsPageSizeChange}
        wellRegionFilter={wellRegionFilter}
        onWellRegionFilter={setWellRegionFilterAndResetPage}
        wellRegionCounts={wellRegionCounts}
        wellsPagedLoading={wellsPagedLoading}
        vendorsPaged={vendorsPaged}
        vendorsPagedTotal={vendorsPagedTotal}
        vendorsPage={vendorsPage}
        vendorsPageSize={vendorsPageSize}
        onVendorsPageChange={setVendorsPage}
        onVendorsPageSizeChange={handleVendorsPageSizeChange}
        vendorsPagedLoading={vendorsPagedLoading}
        authFetch={authFetch}
        newRequestModalOpen={newRequestModalOpen}
        onOpenNewRequest={openNewRequestModal}
        onCloseNewRequestModal={closeNewRequestModal}
        closePanel={closePanel}
        closePoPanel={closePoPanel}
        exportSelectedRequestPdf={exportSelectedRequestPdf}
        exportPurchaseOrderPdfFor={exportPurchaseOrderPdfFor}
        updateSelectedDraft={updateSelectedDraft}
        submitSelectedDraftForApproval={submitSelectedDraftForApproval}
        deleteSelectedDraft={deleteSelectedDraft}
        applyRegionalDecision={applyRegionalDecision}
        queueForProcurement={queueForProcurement}
        rejectProcurementRequest={rejectProcurementRequest}
        createPurchaseOrder={createPurchaseOrder}
        updatePurchaseOrder={updatePurchaseOrder}
        markPoPaid={markPoPaid}
        emailVendorToPo={emailVendorToPo}
        markProcured={markProcured}
        markCompleteFinal={markCompleteFinal}
        activeRegions={activeRegions}
        newRequestSubmitFieldErrors={newRequestSubmitFieldErrors}
        draftSubmitFieldErrors={draftSubmitFieldErrors}
        rfqDetail={rfqDetail}
        rfqLoading={rfqLoading}
        createRfq={createRfq}
        updateRfq={updateRfq}
        sendRfqInvitations={sendRfqInvitations}
        selectVendorForRfq={selectVendorForRfq}
        cancelRfq={cancelRfq}
        canApproveRfqWorkflow={canApproveRfqWorkflow}
        approveRfq={approveRfq}
        rejectRfq={rejectRfq}
        rfqListRows={rfqListRows}
        rfqListLoading={rfqListLoading}
        rfqListPage={rfqListPage}
        rfqListPageSize={rfqListPageSize}
        rfqListTotal={rfqListTotal}
        onRfqListPageChange={setRfqListPage}
        onRfqListPageSizeChange={handleRfqListPageSizeChange}
        selectedRfqRegistryId={selectedRfqRegistryId}
        setSelectedRfqRegistryId={setSelectedRfqRegistryId}
        registryRfqDetail={registryRfqDetail}
        registryRfqDetailLoading={registryRfqDetailLoading}
        poContextRequestForRfq={poContextRequestForRfq}
        rfqRegistryLinkedPo={rfqRegistryLinkedPo}
        onOpenPurchaseOrderFromRfq={(poId) => navigate(`/purchase-orders/${encodeURIComponent(poId)}`)}
      />
      <AppFooter />
      <ToastContainer position="top-right" autoClose={3500} newestOnTop closeOnClick theme="colored" />
    </div>
  );
}

export default App;
